import React, { useContext, useEffect, useRef, useState } from "react"
import { useQueryClient } from "react-query"
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
  sendPasswordResetEmail
} from "firebase/auth"
import { useConfigurationContext } from "contexts/configuration/context"
import { initializeApp, getApp } from "firebase/app"
import { useAuthenticatedUserContext } from "./authenticated-user-context"

type FirebaseAuthContextType = {
  user?: User
  authReady: boolean
  logout: () => Promise<void>
  signin: (email: string, password: string) => Promise<UserCredential>
  sendForgottenPasswordEmail: (email: string) => Promise<void>
}

const stub = (): never => {
  throw new Error(
    "You forgot to wrap your component in <FirebaseAuthContext.Provider>"
  )
}

const FirebaseAuthContext = React.createContext<FirebaseAuthContextType>({
  user: undefined,
  authReady: false,
  logout: stub,
  signin: stub,
  sendForgottenPasswordEmail: stub
})

export const useFirebaseAuthContext = (): FirebaseAuthContextType => {
  const context = useContext(FirebaseAuthContext)

  if (context === undefined) {
    throw new Error(
      "You must wrap your component in `FirebaseAuthContext.Provider` before trying to access this."
    )
  }

  return context
}

export default FirebaseAuthContext

export const FirebaseAuthContextProvider: React.FC = ({ children }) => {
  const configuration = useConfigurationContext()
  const authenticatedUserContext = useAuthenticatedUserContext()

  const queryClient = useQueryClient()

  const [user, setUser] = useState<User | undefined>(undefined)
  const [authReady, setAuthReady] = useState(false)
  const signedInUserID = useRef<string | undefined>(undefined)

  let app

  try {
    app = getApp()
  } catch {
    const firebaseConfig = {
      apiKey: configuration.firebase.apiKey,
      authDomain: configuration.firebase.authDomain,
      projectId: configuration.firebase.projectId,
      appId: configuration.firebase.appId
    }
    app = initializeApp(firebaseConfig)
  }

  const auth = getAuth(app)

  const signin = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password)
  }

  const logout = () => {
    return signOut(auth)
  }

  const sendForgottenPasswordEmail = (email: string) => {
    return sendPasswordResetEmail(auth, email)
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      // the cached data (including the session token, user and branches) belongs to the previous user,
      // so clear it before the next user's data is loaded - eg logging out then in as someone else
      if (
        signedInUserID.current !== undefined &&
        signedInUserID.current !== currentUser?.uid
      ) {
        queryClient.clear()
      }
      signedInUserID.current = currentUser?.uid

      if (currentUser != null) {
        setUser(currentUser)

        authenticatedUserContext.setUser({
          email: currentUser.email || undefined,
          name: currentUser.displayName || undefined,
          id: currentUser.uid || undefined
        })
      } else {
        setUser(undefined)
        authenticatedUserContext.setUser(undefined)
      }
      setAuthReady(true)
    })
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line
  }, [auth])

  return (
    <FirebaseAuthContext.Provider
      value={{ user, authReady, logout, signin, sendForgottenPasswordEmail }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  )
}
